import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { PermissionTypes, Roles } from "enums";

import errorHandling from "core/errorHandling";
import pageContext from "core/pageContext";
import router from "core/router";
import user from "core/user";
import { getIsVerifiedUser } from "core/userInfo/selectors";
import { date } from "core/util";
import { useFormRequestWithValidationError } from "core/hookForm";
import { useHasRole } from "core/hooks";
import {
    CancellationReason,
    ChangeStatus,
    FundsTransferType,
    SignatureStatus,
    SignatureType,
    SubjectTypeEnum,
    VehicleCancellationReason,
} from "types";
import { Pages, Tabs } from "routeConstants";
import { vehicleCancellationApi, vehicleDigiSignApi, changeSheetRecordApi } from "serverApi";

import { DIGISIGN_SUCCESS_EVENT_VALUE, GET_PARAM_EVENT, GET_PARAM_ID_CHANGE_SHEET, GET_PARAM_ID_ENVELOPE } from "../constants";
import getValidationSchema from "../validationSchema";
import { FORM_CONTRACT_CANCELLATION } from "../../../constants";
import { CreateChangeSheetRequest, DigiSignStatus, FormStep, VehicleCancellationFormValues } from "../types";

import { useCancellationReasons } from "./useCancellationReasons";

export function useContractCancellationForm() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { idObject } = useParams();

    const isAuthenticated = useHasRole(Roles.AUTHENTICATED);
    const isSkSite = useSelector(pageContext.getIsSlovakSite);
    const verified = useSelector(getIsVerifiedUser);
    const isOneTimeChange = useSelector(user.getIsOneTimeChangeRole);
    const hasEditPermission = useSelector(user.hasPermission(PermissionTypes.EDIT_CONTRACT_DATA));

    const [isLoading, setIsLoading] = useState(true);
    const [isFeasible, setIsFeasible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formStep, setFormStep] = useState<FormStep>(FormStep.S0_INTRODUCTION);
    const [changeStatus, setChangeStatus] = useState<ChangeStatus>(ChangeStatus.NEW);
    const [containsParallelCancellable, setContainsParallelCancellable] = useState(false);
    const [digitalSignatureFormStatus, setDigitalSignatureFormStatus] = useState<SignatureStatus>(SignatureStatus.UNSIGNED);

    const [digitalSignatureOnlineStatus, setDigitalSignatureOnlineStatus] = useState<DigiSignStatus>("idle");
    const [cancellationReasons, setCancellationReasons] = useState<CancellationReason[]>([]);

    const initialValues: VehicleCancellationFormValues = {
        changeSheetId: null,
        identificationNumberVerify: null,
        cancellationReason: null,
        cancellationDate: null,
        cancellationNote: null,
        fundsTransfer: isAuthenticated ? FundsTransferType.OTHER_BANK_ACCOUNT : null,
        fundsTransferBankAccount: {
            prefix: null,
            accountNumber: null,
            bankCode: null,
            iban: null,
        },
        contactPerson: {
            subjectType: SubjectTypeEnum.INDIVIDUAL,
        },
        signatureType: SignatureType.PHYSICAL,
        dateOfSigning: date.todayDateAsIsoString,
    };

    const validationSchemas = getValidationSchema();
    const validationSchema = validationSchemas[formStep];

    const form = useForm<VehicleCancellationFormValues>({
        defaultValues: initialValues,
        resolver: yupResolver(validationSchema),
        context: { isAuthenticated, isSkSite },
    });

    const { setValue, getValues } = form;
    const signatureType = getValues("signatureType");

    const { getCancellationReasonsForChange } = useCancellationReasons();

    const goToPreviousStep = (newFormStep: FormStep) => {
        setFormStep(newFormStep);
    };

    // Initial loading check
    useEffect(() => {
        const idChangeSheet = searchParams.get(GET_PARAM_ID_CHANGE_SHEET);
        dispatch(errorHandling.removeServiceErrors(FORM_CONTRACT_CANCELLATION));

        async function delay(ms: number) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        }

        async function trySignWithRetries(changeSheetId: string, envelopeId: string, maxAttempts = 3, delayMs = 5000) {
            setDigitalSignatureOnlineStatus("loading");
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                try {
                    const [error, res] = await vehicleDigiSignApi.finalize(changeSheetId, envelopeId);
                    if (error) {
                        throw error;
                    }
                    setDigitalSignatureOnlineStatus("signed");
                    return;
                } catch (error) {
                    if (attempt < maxAttempts) {
                        await delay(delayMs);
                    } else {
                        setDigitalSignatureOnlineStatus("failed");
                    }
                }
            }
        }

        async function handleDigitalSignature() {
            if (!idChangeSheet) return;

            const signEnvelopeId = searchParams.get(GET_PARAM_ID_ENVELOPE);
            const signEvent = searchParams.get(GET_PARAM_EVENT);

            if (signEnvelopeId && signEvent === DIGISIGN_SUCCESS_EVENT_VALUE) {
                trySignWithRetries(idChangeSheet, signEnvelopeId); // Start async retry logic (non-blocking)
            }

            if (signEnvelopeId || signEvent) {
                const updatedParams = new URLSearchParams(searchParams);
                updatedParams.delete(GET_PARAM_ID_ENVELOPE);
                updatedParams.delete(GET_PARAM_EVENT);
                setSearchParams(updatedParams);
            }
        }

        async function loadFormData() {
            if (idChangeSheet) {
                const [error, res] = await changeSheetRecordApi.getChangeSheetRecord(idObject, idChangeSheet);

                const reasons = await getCancellationReasonsForChange(idChangeSheet);
                setCancellationReasons(reasons);

                const changeStatus = res.body?.status;
                setChangeStatus(changeStatus);

                if (changeStatus === ChangeStatus.CREATED) {
                    setFormStep(FormStep.S1_CANCELLATION_PARAMS);
                    const formData = initialValues;
                    formData.changeSheetId = idChangeSheet;

                    form.reset(formData);
                } else if (changeStatus === ChangeStatus.SUBMITTED) {
                    setFormStep(FormStep.S2_REVIEW_SUBMIT);
                    const formData = res.body?.formData;
                    formData.changeSheetId = idChangeSheet;
                    setDigitalSignatureFormStatus(res.body?.signatureStatus ?? SignatureStatus.UNSIGNED);

                    form.reset(formData);
                }
            }
        }

        async function fetchIsFeasible() {
            try {
                const [error, res] = await vehicleCancellationApi.isFeasible(idObject);
                const isFeasibleResponse = res.body.isFeasible;
                const isChangeFeasible: boolean = error ? false : res.body.isFeasible;

                setIsFeasible(isChangeFeasible);
                if (!isFeasibleResponse) {
                    setFormStep(FormStep.NOT_FEASIBLE);
                }

                return isFeasibleResponse;
            } catch (err) {
                setIsFeasible(false);
                return false;
            }
        }

        function checkIfVerified() {
            if (!isOneTimeChange && (!hasEditPermission || !verified)) {
                dispatch(router.navigate(Pages.USER, Tabs.UNVERIFIED_USER, {}, { type: "form" }, false));
            }
        }

        async function prepareFormInitialization() {
            await handleDigitalSignature();
            const isFeasibleResponse = await fetchIsFeasible();
            if (isFeasibleResponse) {
                await loadFormData();
            }
            setIsLoading(false);
        }

        checkIfVerified();
        prepareFormInitialization();
    }, [idObject]);

    const dispatch = useDispatch();

    const onMoveToFormStep = (formStep: FormStep) => () => {
        setFormStep(formStep);
    };

    const handleRequest = useFormRequestWithValidationError(form);

    function onSubmitStep0Validation(data: VehicleCancellationFormValues) {
        setIsSubmitting(true);

        const onSuccess = async (response) => {
            try {
                const changeSheetId = response?.body?.id;
                setContainsParallelCancellable(response?.body?.containsParallelCancellable);
                setValue("changeSheetId", changeSheetId);

                // Add change sheet id to url search params.
                searchParams.set(GET_PARAM_ID_CHANGE_SHEET, changeSheetId);
                setSearchParams(searchParams);

                const cancellationReasons = await getCancellationReasonsForChange(changeSheetId);
                setCancellationReasons(cancellationReasons);

                setChangeStatus(ChangeStatus.CREATED);
                setFormStep(FormStep.S1_CANCELLATION_PARAMS);
            } catch (e) {
                dispatch(errorHandling.addServiceError(FORM_CONTRACT_CANCELLATION, null));
            } finally {
                setIsSubmitting(false);
            }
        };

        const onValidationError = () => {
            setIsSubmitting(false);
        };

        const onError = () => {
            dispatch(errorHandling.addServiceError(FORM_CONTRACT_CANCELLATION, null));
            setIsSubmitting(false);
        };

        const requestDto: CreateChangeSheetRequest = {
            identificationNumberVerify: data.identificationNumberVerify,
        };

        handleRequest(() => vehicleCancellationApi.createCancellation(idObject, requestDto), onSuccess, onValidationError, onError);
    }

    async function onSubmitCancellationParams(data: VehicleCancellationFormValues) {
        const onSuccess = () => {
            setChangeStatus(ChangeStatus.SUBMITTED);
            setIsSubmitting(false);
        };

        const onError = () => {
            // dispatch(errorHandling.addServiceError(FORM_CONTRACT_CANCELLATION, null));
            setIsSubmitting(false);
        };

        const onValidationError = () => {
            setIsSubmitting(false);
        };

        setIsSubmitting(true);
        const requestDto = {
            cancellationReason: data.cancellationReason,
            cancellationDate: data.cancellationDate,

            cancellationNote: data.cancellationReason === VehicleCancellationReason.BY_AGREEMENT ? data.cancellationNote : null,
            contactPerson: data.cancellationReason === VehicleCancellationReason.INSURER_DEATH ? data.contactPerson : null,
            vehicleRemovedFromRegistrationStatement:
                data.cancellationReason === VehicleCancellationReason.REMOVED_FROM_REGISTER
                    ? data.vehicleRemovedFromRegistrationStatement
                    : null,

            fundsTransfer: data.fundsTransfer,
            fundsTransferBankAccount: data.fundsTransfer === FundsTransferType.OTHER_BANK_ACCOUNT ? data.fundsTransferBankAccount : null,

            signatureType: data.signatureType,
            dateOfSigning: data.signatureType === SignatureType.PHYSICAL ? data.dateOfSigning : null,
            placeOfSigning: data.signatureType === SignatureType.PHYSICAL ? data.placeOfSigning : null,
        };

        await handleRequest(
            () => vehicleCancellationApi.submitCancellation(idObject, data.changeSheetId, requestDto),
            onSuccess,
            onValidationError,
            onError,
        );
    }

    async function onCompleteCancellation(data: VehicleCancellationFormValues) {
        const onSuccess = () => {
            setChangeStatus(ChangeStatus.COMPLETED);
            setIsSubmitting(false);
        };

        const onError = () => {
            // dispatch(errorHandling.addServiceError(FORM_CONTRACT_CANCELLATION, null));
            setIsSubmitting(false);
        };

        const onValidationError = () => {
            setIsSubmitting(false);
        };

        setIsSubmitting(true);
        const requestDto = {
            signature: data.signature,
        };

        await handleRequest(
            () => vehicleCancellationApi.completeCancellation(idObject, data.changeSheetId, requestDto),
            onSuccess,
            onValidationError,
            onError,
        );
    }

    return {
        form,
        signatureType,

        onMoveToFormStep,
        formStep,
        setFormStep,
        containsParallelCancellable,
        digitalSignatureFormStatus,
        digitalSignatureOnlineStatus,

        onSubmitStep0Validation,

        isLoading,
        isFeasible,
        isSubmitting,

        goToPreviousStep,
        cancellationReasons,
        changeStatus,
        onSubmitCancellationParams,
        onCompleteCancellation,
    };
}
